/**
 * Lakehouse Storage API Client
 *
 * Frontend API client for managing lakehouse-storage account mappings.
 * Part of Phase 8: Lakehouse Storage Mapping.
 */

import { apiClient } from '../../../services/apiClient';

// Types matching backend
export type StorageAccountPurpose = 'primary' | 'secondary' | 'archive' | 'overflow';
export type StorageMappingStatus = 'active' | 'inactive' | 'pending_removal';

export interface LakehouseStorageMapping {
  id: string;
  lakehouseId: string;
  storageAccountId: string;
  purpose: StorageAccountPurpose;
  isPrimary: boolean;
  status: StorageMappingStatus;
  createdAt: string;
  updatedAt: string;
  // Extended storage account info (from joined query)
  storageAccountName?: string;
  storageAccountType?: string;
  containerName?: string;
  region?: string;
  // Backup statistics
  backupCount?: number;
  totalStorageBytes?: number;
  lastBackupAt?: string | null;
}

export interface StorageAccountDetails {
  id: string;
  name: string;
  type: string;
  containerName: string;
  region: string;
  isActive: boolean;
}

export interface AddStorageAccountInput {
  storageAccountId: string;
  purpose?: StorageAccountPurpose;
  isPrimary?: boolean;
}

export interface UpdateStorageMappingInput {
  purpose?: StorageAccountPurpose;
  isPrimary?: boolean;
  status?: StorageMappingStatus;
}

export interface ActiveBackupCheck {
  hasActiveBackups: boolean;
  backupCount: number;
  lastBackupAt: string | null;
  oldestBackupAt: string | null;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface ApiMessageResponse {
  success: boolean;
  message: string;
  error?: string;
}

/**
 * Get all storage accounts mapped to a lakehouse
 */
export async function getStorageAccountsForLakehouse(
  lakehouseId: string
): Promise<LakehouseStorageMapping[]> {
  const response = await apiClient.get<ApiResponse<LakehouseStorageMapping[]>>(
    `/api/v1/lakehouses/${lakehouseId}/storage-accounts`
  );
  return response.data.data;
}

/**
 * Get the primary storage account for a lakehouse
 */
export async function getPrimaryStorageAccount(
  lakehouseId: string
): Promise<LakehouseStorageMapping | null> {
  try {
    const response = await apiClient.get<ApiResponse<LakehouseStorageMapping>>(
      `/api/v1/lakehouses/${lakehouseId}/storage-accounts/primary`
    );
    return response.data.data;
  } catch (error: unknown) {
    // If 404, return null (no storage account found)
    const axiosError = error as { response?: { status?: number } };
    if (axiosError.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Add a storage account to a lakehouse
 */
export async function addStorageAccount(
  lakehouseId: string,
  input: AddStorageAccountInput
): Promise<LakehouseStorageMapping> {
  const response = await apiClient.post<ApiResponse<LakehouseStorageMapping>>(
    `/api/v1/lakehouses/${lakehouseId}/storage-accounts`,
    input
  );
  return response.data.data;
}

/**
 * Update a storage account mapping
 */
export async function updateStorageMapping(
  lakehouseId: string,
  mappingId: string,
  input: UpdateStorageMappingInput
): Promise<LakehouseStorageMapping> {
  const response = await apiClient.put<ApiResponse<LakehouseStorageMapping>>(
    `/api/v1/lakehouses/${lakehouseId}/storage-accounts/${mappingId}`,
    input
  );
  return response.data.data;
}

/**
 * Remove a storage account from a lakehouse
 *
 * @param lakehouseId - The lakehouse ID
 * @param mappingId - The mapping ID to remove
 * @param force - Force removal even if backups exist (will delete backup records)
 */
export async function removeStorageAccount(
  lakehouseId: string,
  mappingId: string,
  force: boolean = false
): Promise<{ message: string }> {
  const response = await apiClient.delete<ApiMessageResponse>(
    `/api/v1/lakehouses/${lakehouseId}/storage-accounts/${mappingId}`,
    { params: { force: force ? 'true' : undefined } }
  );
  return { message: response.data.message };
}

/**
 * Set a storage account as the primary for a lakehouse
 */
export async function setAsPrimary(
  lakehouseId: string,
  mappingId: string
): Promise<LakehouseStorageMapping> {
  return updateStorageMapping(lakehouseId, mappingId, { isPrimary: true });
}

/**
 * Change the purpose of a storage account mapping
 */
export async function changePurpose(
  lakehouseId: string,
  mappingId: string,
  purpose: StorageAccountPurpose
): Promise<LakehouseStorageMapping> {
  return updateStorageMapping(lakehouseId, mappingId, { purpose });
}

/**
 * Deactivate a storage account mapping (mark as inactive)
 */
export async function deactivateMapping(
  lakehouseId: string,
  mappingId: string
): Promise<LakehouseStorageMapping> {
  return updateStorageMapping(lakehouseId, mappingId, { status: 'inactive' });
}

/**
 * Reactivate a storage account mapping
 */
export async function reactivateMapping(
  lakehouseId: string,
  mappingId: string
): Promise<LakehouseStorageMapping> {
  return updateStorageMapping(lakehouseId, mappingId, { status: 'active' });
}

// Convenience type for component props
export type LakehouseStorageApi = {
  getStorageAccountsForLakehouse: typeof getStorageAccountsForLakehouse;
  getPrimaryStorageAccount: typeof getPrimaryStorageAccount;
  addStorageAccount: typeof addStorageAccount;
  updateStorageMapping: typeof updateStorageMapping;
  removeStorageAccount: typeof removeStorageAccount;
  setAsPrimary: typeof setAsPrimary;
  changePurpose: typeof changePurpose;
  deactivateMapping: typeof deactivateMapping;
  reactivateMapping: typeof reactivateMapping;
};

export const lakehouseStorageApi: LakehouseStorageApi = {
  getStorageAccountsForLakehouse,
  getPrimaryStorageAccount,
  addStorageAccount,
  updateStorageMapping,
  removeStorageAccount,
  setAsPrimary,
  changePurpose,
  deactivateMapping,
  reactivateMapping,
};

export default lakehouseStorageApi;
